import React, { useEffect } from 'react';
import { ArrowLeft, Calendar, Clock, User, Tag, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const BlogPost9 = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/credentra-logo-new.png" 
                alt="Credentra Logo" 
                className="h-8 w-auto"
              />
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center text-gray-600 hover:text-credentra-bright-teal transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Resources
            </Link>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Article Header */}
          <div className="px-8 py-12 border-b border-gray-100">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                January 15, 2025
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                6 min read
              </div>
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                Jonathan Michael
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-credentra-dark-teal mb-6 leading-tight">
              The FICA Tip Credit Gets a Makeover: Now Available for Salons and Beauty Businesses
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              For years, the FICA Tip Credit has been something of a "hidden gem" in the restaurant industry. Now, that same opportunity is opening up for an entirely new group of business owners.
            </p>
            
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <Tag className="w-3 h-3 mr-1" />
                FICA
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <Tag className="w-3 h-3 mr-1" />
                Beauty Salons
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                <Tag className="w-3 h-3 mr-1" />
                Tax Credits
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                <Tag className="w-3 h-3 mr-1" />
                2025 Law Changes
              </span>
            </div>
          </div>

          {/* Article Body */}
          <div className="px-8 py-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                If your servers or bartenders earned tips, you could claim a tax credit to offset the employer's share of Social Security and Medicare taxes you paid on those tips. It wasn't flashy, but for many restaurants, it quietly saved thousands of dollars each year.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Now, that same opportunity is opening up for an entirely new group of business owners.
              </p>

              <div className="bg-credentra-bright-teal/5 border-l-4 border-credentra-bright-teal p-6 my-8 rounded-r-lg">
                <p className="text-lg font-semibold text-credentra-dark-teal mb-2">
                  Key Legislative Change
                </p>
                <p className="text-gray-700">
                  On <strong>July 4, 2025</strong>, Congress passed the <strong>One Big Beautiful Bill Act (Public Law 119-21)</strong>. Tucked into that wide-ranging legislation was a simple but powerful change: the <strong>FICA Tip Credit (Internal Revenue Code §45B) was expanded beyond restaurants and bars to include beauty service businesses</strong>. That means salons, barbershops, nail salons, spas, and similar establishments are now eligible.
                </p>
                <p className="text-gray-700 mt-3 font-medium">
                  The credit applies beginning with <strong>tax years starting January 1, 2025</strong>.
                </p>
              </div>

              <h2 className="text-3xl font-bold text-credentra-dark-teal mb-6 mt-12">
                What changed?
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Until now, §45B was reserved for the food and beverage world. The logic was straightforward: tipped employees meant higher payroll taxes for employers, so the IRS allowed businesses to claim a credit equal to <strong>7.65% of reported tips</strong> (after subtracting the portion of tips that count toward meeting the minimum wage).
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                With the new law, beauty industry owners who pay FICA taxes on tips can finally claim that same credit.
              </p>

              <h2 className="text-3xl font-bold text-credentra-dark-teal mb-6 mt-12">
                Why it matters for salons and spas
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                If you run a salon, you know how central tipping is to your business model. Stylists, barbers, and nail techs often earn a significant share of their income through tips. As the employer, you're on the hook for the payroll taxes—regardless of whether those tips come out of your register or not.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Now, instead of swallowing that cost, you can claim a dollar-for-dollar credit against your income tax. For many small salons, that's not a rounding error — it's real cash that can be reinvested.
              </p>

              <div className="bg-gray-50 p-8 rounded-xl my-8 border border-gray-200">
                <h3 className="text-xl font-semibold text-credentra-dark-teal mb-4">
                  Quick Example:
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-credentra-bright-teal font-bold mr-3">•</span>
                    A mid-sized salon with <strong>$50,000 in reported tips</strong> over the year.
                  </li>
                  <li className="flex items-start">
                    <span className="text-credentra-bright-teal font-bold mr-3">•</span>
                    Employer's share of FICA taxes = <strong>7.65%</strong>, or about <strong>$3,825</strong>.
                  </li>
                  <li className="flex items-start">
                    <span className="text-credentra-bright-teal font-bold mr-3">•</span>
                    Under §45B, that <strong>$3,825 becomes a tax credit</strong> on your return.
                  </li>
                </ul>
                <p className="mt-4 text-gray-700 font-medium">
                  That's money you keep — and unlike a deduction, a credit directly reduces your tax liability dollar-for-dollar.
                </p>
              </div>

              <h2 className="text-3xl font-bold text-credentra-dark-teal mb-6 mt-12">
                What salon owners should do next
              </h2>
              
              <ol className="space-y-6 text-lg text-gray-700 mb-8">
                <li className="flex items-start">
                  <span className="bg-credentra-bright-teal text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1 text-sm">1</span>
                  <div>
                    <strong>Talk with your payroll provider.</strong> Make sure tips are being properly tracked, reported, and subject to FICA.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-credentra-bright-teal text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1 text-sm">2</span>
                  <div>
                    <strong>Coordinate with your CPA.</strong> The credit is claimed on <strong>Form 8846</strong> (Credit for Employer Social Security and Medicare Taxes Paid on Certain Employee Tips), and then carried to <strong>Form 3800</strong>.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-credentra-bright-teal text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1 text-sm">3</span>
                  <div>
                    <strong>Get your documentation straight.</strong> The IRS will expect clean records tying tips to payroll taxes. Having a reliable process for data capture now will make filing smoother come 2026 (for the 2025 tax year).
                  </div>
                </li>
              </ol>

              <h2 className="text-3xl font-bold text-credentra-dark-teal mb-6 mt-12">
                The bottom line
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The restaurant industry has enjoyed the FICA Tip Credit for decades. Starting <strong>January 1, 2025</strong>, beauty service businesses will finally get the same relief. For salon owners, this is a quiet but meaningful way to lower your tax bill and put more money back into your business.
              </p>

              <p className="text-xl font-semibold text-credentra-dark-teal leading-relaxed">
                It's one more reminder that in the world of taxes, what you don't know can cost you.
              </p>
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-gray-100 px-8 py-8 border-t border-gray-100">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-credentra-dark-teal mb-4">
                Ready to claim your salon's FICA tip credits?
              </h3>
              <p className="text-gray-600 mb-6">
                Join hundreds of salon owners who are already maximizing their tax savings.
              </p>
              <button 
                onClick={() => window.open('https://form.typeform.com/to/mstSUQPT', '_blank')}
                className="inline-flex items-center text-white font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                style={{background: 'linear-gradient(135deg, #00d6cf, #3c778d)', boxShadow: '0 4px 20px rgba(0, 214, 207, 0.3)'}}
              >
                Get Started Today
                <ExternalLink className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="text-center mt-12">
          <button
            onClick={scrollToTop}
            className="text-credentra-bright-teal hover:text-credentra-dark-teal transition-colors"
          >
            Back to top
          </button>
        </div>
      </article>
    </div>
  );
};

export default BlogPost9;